import { useRef } from 'react';
import { useIntl } from 'react-intl';
import { useAtom } from 'jotai';

import { useConnectedAppsQuery } from 'src/api/queries/connectedAppsQueries';
import { externalAgentAtom } from 'src/shared/externalAgent';
import {
  connectorStatus,
  DEFAULT_PROMPT_ID,
  ExternalPromptId,
  MCP_SETUP_URL,
  resolveExternalAgent,
} from './externalAgents';
import messages from './messages';

const SELF = { kind: 'self' } as const;

function openInNewTab(url: string) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * The chosen external agent and whether the user has connected FileSpin to
 * it, shared by the header button and the AI Agent panel's empty state.
 *
 * `openPrimary` is what a plain click does: open the agent with the
 * get-started prompt once connected, otherwise the setup guide — a prompt
 * sent to an agent without the connector has nothing to work with.
 */
export function useExternalAgent() {
  const { formatMessage } = useIntl();
  const [agentId, setAgentId] = useAtom(externalAgentAtom);
  const agent = resolveExternalAgent(agentId);

  // While not connected, refetch on every focus, fresh or not: the user
  // typically connects in the agent's own tab and comes straight back, and
  // the button should have caught up by then. Once connected, the usual
  // stale-only refetch is enough.
  const awaitingConnect = useRef(false);
  const { data } = useConnectedAppsQuery(SELF, undefined, {
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: awaitingConnect.current ? 'always' : true,
  });
  const status = connectorStatus(agent, data?.data);
  awaitingConnect.current = status === 'not_connected';

  const openPrompt = (id: ExternalPromptId) =>
    openInNewTab(
      agent.prefillUrl(formatMessage(messages[`prompt_${id}_text` as keyof typeof messages])),
    );
  const openSetup = () => openInNewTab(MCP_SETUP_URL);
  const openPrimary = () =>
    status === 'not_connected' ? openSetup() : openPrompt(DEFAULT_PROMPT_ID);

  return { agent, setAgentId, status, openPrompt, openSetup, openPrimary };
}
